import { useMutation } from '@apollo/client';
import * as _ from 'lodash-es';
import Quill, { Delta, Parchment } from 'quill/core';
import type { ReactNode } from 'react';
import React, { useContext, useMemo } from 'react';
import { useDebounce } from 'use-debounce';
import PrefixTriggeredMenu from './PrefixTriggeredMenu';
import { useIsNonVisitorUser } from '../../account/useIsLoggedIn';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import { gql } from '../../graphql/generated/gql';
import type { DocumentType } from '../../graphql/generated/gql';
import GroupSelectOption from '../../groups/GroupSelectOption';
import {
  PostPolicyAction,
  usePostPolicyCheck,
} from '../../post/postPolicyCheck';
import PostSelectOption from '../../post/PostSelectOption';
import type {
  UseEntitySearchConfig,
  UseEntitySearchResult,
} from '../../search/entitySearch/useEntitySearch';
import useEntitySearch from '../../search/entitySearch/useEntitySearch';
import TopicSelectOption from '../../topic/TopicSelectOption';
import { UIActions } from '../../ui/uiSlice';
import UserSelectOption from '../../user/UserSelectOption';
import { ENDING_PUNCTUATIONS } from '../constants';
import NewPostSelectOption from '../NewPostSelectOption';
import { usePrefixListener } from '../PrefixListener';
import type QuillEditor from '../QuillEditor';

const CREATE_POST_MUTATION = gql(/** @complexity 2 */ `
  mutation MentionPicker_createPost(
    $title: String
  ) {
    post: createPost(
      title: $title
    ) {
      id
    }
  }
`);

const NEW_POST_OPTION_IDENTITY = 'new-post';

const shouldAppendSpace = (quill: Quill, index: number) => {
  const [leaf, offset] = quill.getLeaf(index);

  if (leaf instanceof Parchment.TextBlot) {
    const followingChar = leaf.value()[offset];
    if (
      followingChar === ' ' ||
      (followingChar && ENDING_PUNCTUATIONS.has(followingChar))
    ) {
      return false;
    }
  }

  return true;
};

const handleMention = ({
  quill,
  text,
  id,
  type,
  mentionIndex,
  mentionContent,
}: {
  quill: Quill;
  text: string;
  id: string;
  type: string;
  mentionIndex: number;
  mentionContent: string;
}) => {
  const index = mentionIndex - text.length;

  const delta = new Delta()
    .retain(index)
    .delete(text.length)
    .insert({ mention: { id, type } }, { content: mentionContent });

  if (shouldAppendSpace(quill, mentionIndex)) {
    delta.insert(' ', quill.getFormat(index));
  }

  quill.updateContents(delta, Quill.sources.USER);
};

const ENTITY_COMPONENTS = {
  post: PostSelectOption,
  topic: TopicSelectOption,
  user: UserSelectOption,
  group: GroupSelectOption,
} as const;

const USE_PREFIX_LISTENER_OPTIONS = ['@', '+'];

const POST_FRAGMENT = gql(`
  fragment MentionPickerProvider_post on Post {
    id
    ...usePostPolicyCheck_post
  }
`);

const MentionPickerContext = React.createContext<DocumentType<
  typeof POST_FRAGMENT
> | null>(null);

interface MentionPickerProviderProps {
  children: ReactNode;
  post: DocumentType<typeof POST_FRAGMENT>;
}

export const MentionPickerProvider = ({
  children,
  post,
}: MentionPickerProviderProps) => (
  <MentionPickerContext.Provider value={post}>
    {children}
  </MentionPickerContext.Provider>
);

interface MentionPickerProps {
  quill: QuillEditor;
  readOnly: boolean;
}

const MentionPicker = ({ quill, readOnly }: MentionPickerProps) => {
  const post = useContext(MentionPickerContext);
  const postPolicyCheck = usePostPolicyCheck();

  const isNonVisitorUser = useIsNonVisitorUser();

  const { clientRects, text, prefix, reset } = usePrefixListener(
    quill,
    USE_PREFIX_LISTENER_OPTIONS,
  );
  const [query] = useDebounce(text || '', 100);

  const dispatch = useAppDispatch();
  const recentlyMentionedUserIds = useAppSelector(
    (state) => state.ui.recentlyMentionedUserIds,
  );

  const config: UseEntitySearchConfig = useMemo(() => {
    if (!prefix) {
      return {};
    }

    return {
      user: {
        sortBy: (u1, u2) => {
          if (!post) return 0;
          const hasUser1Access = postPolicyCheck(
            PostPolicyAction.ViewPost,
            post,
            u1,
          );
          const hasUser2Access = postPolicyCheck(
            PostPolicyAction.ViewPost,
            post,
            u2,
          );
          if (hasUser1Access === hasUser2Access) return 0;
          return hasUser1Access ? -1 : 1;
        },
        extra: (user) => ({
          noAccess:
            !!post && !postPolicyCheck(PostPolicyAction.ViewPost, post, user),
        }),
      },
      post: {},
      topic: {},
      group: {},
      order:
        prefix === '@'
          ? ['user', 'group', 'post', 'topic']
          : ['post', 'topic', 'user', 'group'],
    };
  }, [post, postPolicyCheck, prefix]);

  let searchResults = useEntitySearch(query, config);

  const [createPost] = useMutation(CREATE_POST_MUTATION);

  if (readOnly || !clientRects || !isNonVisitorUser) return null;

  if (prefix === '@') {
    const [recentlyMentionedSearchResults, remainingSearchResults] =
      _.partition(searchResults, ({ id }) =>
        recentlyMentionedUserIds.includes(id),
      );

    searchResults = [
      ...recentlyMentionedSearchResults.toSorted((a, b) => {
        if (a.score === b.score) {
          return (
            recentlyMentionedUserIds.indexOf(a.id) -
            recentlyMentionedUserIds.indexOf(b.id)
          );
        }
        return b.score - a.score;
      }),
      ...remainingSearchResults,
    ];
  }

  const items = searchResults.map<
    | { id: string; data: UseEntitySearchResult & { itemType: 'entity' } }
    | {
        id: typeof NEW_POST_OPTION_IDENTITY;
        data: { itemType: typeof NEW_POST_OPTION_IDENTITY; name: string };
      }
  >((result) => ({
    id: result.id,
    data: { ...result, itemType: 'entity' },
  }));

  if (prefix === '+' && text) {
    items.push({
      id: NEW_POST_OPTION_IDENTITY,
      data: { name: text, itemType: NEW_POST_OPTION_IDENTITY },
    });
  }

  return (
    <PrefixTriggeredMenu<(typeof items)[number]['data']>
      quill={quill}
      onSelect={async ({ data }) => {
        const range = quill.getSelection(true);
        if (!range) return;

        let id: string | undefined;
        let type: UseEntitySearchResult['type'];
        const { name } = data;

        if (data.itemType === NEW_POST_OPTION_IDENTITY) {
          const newPost = (
            await createPost({
              variables: { title: data.name },
            })
          ).data?.post;
          id = newPost?.id;
          type = 'post';
        } else {
          id = data.id;
          type = data.type;
        }

        if (id) {
          handleMention({
            id,
            type,
            text: prefix + text,
            mentionIndex: range.index,
            mentionContent: name,
            quill,
          });
          if (type === 'user') {
            dispatch(UIActions.addMentionedUser(id));
          }
        }
      }}
      sections={[{ items }]}
      query={query}
      onClose={reset}
      clientRects={clientRects}
      renderItem={(data, { focused }) => {
        if (data.itemType === NEW_POST_OPTION_IDENTITY) {
          return <NewPostSelectOption title={data.name} />;
        }

        const Component = ENTITY_COMPONENTS[data.type];

        // @ts-expect-error
        return <Component {...data} focused={focused} />;
      }}
      testId="mentionPicker"
    />
  );
};

export default MentionPicker;
